import moment from "moment";
import { SHIFTS } from "@/constants/shifts";
import { STATE_RANK } from "../group/hasGroupUserRows";

/**
 * "Radnici" za firme sa ugovorom "više restorana" (telefon i računar): redovi radnika sa poručeno / preuzeto /
 * razlikom i statusom, čipovi statusa sa brojevima i zbirovi. Zahteva nasleđen stari tab
 * more-restaurants/tabs/ReportMealsTabEmployees (report, users, orderStatus, statusItems, selectedCompany,
 * selectedShifts, filters, formatShift, customSearch). Filter statusa je u starom tabu (orderStatus), a pretraga,
 * izbor smena i prikaz su ovde.
 */
export default {
  computed: {
    isSingleDay() {
      return this.filters.dates.from === this.filters.dates.to;
    },
    /** Čipovi statusa sa brojevima; vrednosti su isti statusi kao u starom izborniku (orderStatus). */
    statusChips() {
      const users = (this.report && this.report.users) || [];
      const ordered = users.filter((user) => (user.order_count || 0) > 0);
      const taken = ordered.filter((user) => (user.order_payed || 0) > 0).length;
      const counts = { 0: users.length, 1: ordered.length, 2: users.length - ordered.length, 3: taken, 4: ordered.length - taken };
      return this.statusItems.map((item) => ({ value: item.value, label: item.text, count: counts[item.value] }));
    },
    /** Smene 1–3 uvek (ako su izabrane), četvrta samo kad neko u njoj ima narudžbu. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) &&
          (shift.id < 4 || this.users.some((user) => Number(user[shift.key]) > 0))
      );
    },
    hasExternalCode() {
      return this.users.some((user) => user.external_code);
    },
    allRows() {
      return this.users.map((user, index) => this.toRow(user, index));
    },
    /** Radnici (redovi) posle pretrage, po imenu. */
    matchedRows() {
      const search = this.filters.search;
      const matched = search
        ? this.customSearch(this.allRows, [{ value: "name" }, { value: "idText" }, { value: "externalCode" }], search)
        : this.allRows;
      return [...matched].sort((a, b) => a.name.localeCompare(b.name));
    },
    sums() {
      const sums = { ordered: 0, taken: 0, difference: 0 };
      SHIFTS.forEach((shift) => {
        sums[shift.key] = 0;
      });
      this.matchedRows.forEach((row) => {
        sums.ordered += row.ordered;
        sums.taken += row.taken;
        sums.difference += row.difference;
        SHIFTS.forEach((shift) => {
          sums[shift.key] += row[shift.key];
        });
      });
      return sums;
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.report && this.report.users && this.report.users.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  methods: {
    /** Red radnika: radnik iz starog taba (users) + stanje narudžbe i preuzimanja. */
    toRow(user, index) {
      const ordered = user.order_count || 0;
      const taken = user.order_payed || 0;
      const state = ordered <= 0 ? "off" : taken <= 0 ? "none" : taken >= ordered ? "all" : "part";
      const stamp = user.date ? moment(user.date, "DD.MM.YYYY HH:mm") : null;
      const name = (user.name || "").trim();
      const shift = this.formatShift(user.shift);
      const row = {
        key: `${user.id}-${index}`,
        id: user.id,
        idText: String(user.id),
        externalCode: user.external_code || "",
        name,
        title: user.external_code ? `${name} · ${user.external_code}` : name,
        initials: name
          .split(" ")
          .filter(Boolean)
          .map((part) => part.charAt(0))
          .slice(0, 2)
          .join(""),
        time: stamp && stamp.isValid() ? stamp.format("DD.MM. HH:mm") : "",
        sortTime: stamp && stamp.isValid() ? stamp.valueOf() : 0,
        shift: shift === "" || shift == null ? null : Number(shift) || null,
        ordered,
        taken,
        difference: ordered - taken,
        state,
        stateRank: STATE_RANK[state],
        statusLabel: this.statusLabel(state, taken, ordered),
        source: user,
      };
      SHIFTS.forEach((item) => {
        row[item.key] = Number(user[item.key]) || 0;
      });
      return row;
    },
    statusLabel(state, taken, ordered) {
      if (state === "off") {
        return this.$t("report_desktop.status_not_ordered");
      }
      if (state === "all") {
        return this.$t("report_mobile.status_taken");
      }
      if (state === "part") {
        return this.$t("report_mobile.taken_partly", { taken, total: ordered });
      }
      return this.$t("report_mobile.status_not_taken");
    },
    /** Radnik za mesečni pregled (store: report/selectedEmployee); mesec iz istog izvora kao stari dijalog. */
    toEmployeeContext(row) {
      const company = this.selectedCompany;
      return {
        id: row.id,
        name: row.name,
        lastname: "",
        fullName: row.name,
        companyId: company && company.id,
        companyName: this.toLatin((company && (company.name || company.company)) || ""),
        monthUrl: "/user/more-restaurants-orders-by-month",
      };
    },
  },
};
